import React, { useState, useEffect, useCallback } from 'react';
import { History, RotateCcw, Clock, ChevronDown, ChevronUp, AlertTriangle, CheckCircle, X } from 'lucide-react';
import axios from 'axios';

const API = window.__API_BASE;

const ACTION_LABELS = {
  submit_script: 'Script Upload',
  generate_shots: 'Shot Breakdown',
  generate_prompts: 'Prompt Generation',
  generate_sketches: 'Sketch Generation',
  generate_keyframes: 'Keyframe Generation',
  generate_videos: 'Video Generation',
  generate_assets: 'Asset Generation',
  advance_stage: 'Stage Advance',
  vlm_qa: 'VLM QA',
  post_production_render: 'Video Export',
  delete_project: 'Project Delete',
  pre_rollback: 'Pre-Rollback Safety',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function SnapshotHistory({ projectId, onRollbackComplete }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [rolling, setRolling] = useState(null);

  const fetchSnapshots = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/projects/${projectId}/snapshots`);
      setSnapshots(res.data?.snapshots || []);
    } catch {
      setSnapshots([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (expanded) fetchSnapshots();
  }, [expanded, fetchSnapshots]);

  const handleRollback = async (snapshotId, snapshotNum) => {
    setRolling(snapshotId);
    try {
      const res = await axios.post(`${API}/api/projects/${projectId}/rollback/${snapshotId}`);
      if (res.data?.success) {
        alert(`Rolled back to Snapshot #${snapshotNum}\n\nRestored stage: ${res.data.restored_stage}\nRestored shots: ${res.data.restored_shots}`);
        fetchSnapshots();
        onRollbackComplete?.();
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Rollback failed');
    } finally {
      setRolling(null);
    }
  };

  return (
    <div className="border border-ai-border rounded-lg overflow-hidden" data-testid="snapshot-history">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-ai-bg-secondary/50 transition-colors"
        data-testid="snapshot-toggle"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-ai-accent" />
          <span className="text-sm font-medium text-ai-text">Project History</span>
          {snapshots.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-ai-accent/20 text-ai-accent">{snapshots.length}</span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-ai-text-secondary" /> : <ChevronDown className="w-4 h-4 text-ai-text-secondary" />}
      </button>

      {expanded && (
        <div className="border-t border-ai-border max-h-[320px] overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-xs text-ai-text-secondary">Loading snapshots...</div>
          ) : snapshots.length === 0 ? (
            <div className="p-4 text-center text-xs text-ai-text-secondary">
              No snapshots yet. Snapshots are created automatically before destructive operations.
            </div>
          ) : (
            <div className="divide-y divide-ai-border/50">
              {snapshots.map((snap) => (
                <div
                  key={snap.id}
                  className={`px-4 py-3 flex items-start justify-between gap-3 hover:bg-ai-bg-secondary/30 transition-colors ${
                    snap.trigger_action === 'pre_rollback' ? 'opacity-60' : ''
                  }`}
                  data-testid={`snapshot-${snap.snapshot_number}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-medium text-ai-text">
                        #{snap.snapshot_number}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-ai-bg-secondary text-ai-text-secondary">
                        {ACTION_LABELS[snap.trigger_action] || snap.trigger_action}
                      </span>
                      {snap.restored_at && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 flex items-center gap-0.5">
                          <CheckCircle className="w-2.5 h-2.5" /> Restored
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-ai-text-secondary">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {timeAgo(snap.created_at)}
                      </span>
                      <span>Stage {snap.stage_before}</span>
                      <span>{snap.shot_count} shots</span>
                    </div>
                  </div>
                  {snap.trigger_action !== 'pre_rollback' && (
                    <button
                      onClick={() => handleRollback(snap.id, snap.snapshot_number)}
                      disabled={rolling === snap.id}
                      className="shrink-0 px-2.5 py-1.5 rounded text-[11px] font-medium bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 border border-amber-500/30 transition-all flex items-center gap-1 disabled:opacity-50"
                      data-testid={`rollback-btn-${snap.snapshot_number}`}
                    >
                      <RotateCcw className={`w-3 h-3 ${rolling === snap.id ? 'animate-spin' : ''}`} />
                      {rolling === snap.id ? 'Restoring...' : 'Restore'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
